import { QueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';

import { isSSR } from '../helpers';
import { createQueryClient } from '../query-client';
import {
    AUTH_STATUS_COOKIE_NAME,
    BP_ID_COOKIE_NAME,
    HYBRIS_SESSION_EXPIRE_COOKIE_NAME,
    QUERY_KEY_USER_DATA,
} from './constants';
import { UserData } from './model';

export const getUserIdFromUserData = (userData?: UserData | null): string | 'anonymous' =>
    userData?.email || 'anonymous';

const getUserDataFromQuery = (queryClient: QueryClient) => queryClient.getQueryData<UserData>([QUERY_KEY_USER_DATA]);

const getUserIdFromQuery = () => getUserIdFromUserData(getUserDataFromQuery(createQueryClient()));

/** @name getUserId
 * returns logged-in users email OR for not logged-in user 'anonymous'.
 * firstly extracts data from Hybris solution (because it Hybris server-side prefilled);
 * or fallbacks to react-query solution (CSR only), because  Hybris is not present on Next.js apps
 * @see QUERY_KEY_USER_DATA
 * @see useUserData */
export const getUserId = async (): Promise<string | 'anonymous'> => {
    if (process.env.IS_NEXT === 'true') {
        if (isSSR()) {
            throw new Error('Not allowed! Accessing user specific data in server side');
        }
        const userIdFromQuery: string | 'anonymous' = getIsUserAuthorized() ? getUserIdFromQuery() : 'anonymous';

        return userIdFromQuery;
    }

    const userIdFromHybris = (await window.TC?.sso?.isAuthorized())
        ? (await window.TC.sso.getInitializedUserData())?.email
        : 'anonymous';

    return userIdFromHybris;
};

/** @name getIsUserAuthorized
 *  shows if the user is logged-in
 * */
export const getIsUserAuthorized = (): boolean => {
    if (typeof window !== 'undefined' && (window?.ACC?.userAuthorized || window?.TC?.config?.sso?.isAuthorized)) {
        // used in styleguide (Hybris), e.g. when page is accessed with internal URL https://hybris-qas.in.telecom.lt:9002
        return true;
    }

    if (process.env.IS_NEXT === 'true' && isSSR()) {
        if (process.env.NODE_ENV !== 'production') {
            throw new Error('Not allowed! Accessing user specific data in server side');
        }

        return false;
    }

    // used where Hybris is not present, ex. in Next.js client side
    return (
        (!!Cookies.get(AUTH_STATUS_COOKIE_NAME) && Cookies.get(AUTH_STATUS_COOKIE_NAME) !== 'false') ||
        !!Cookies.get(HYBRIS_SESSION_EXPIRE_COOKIE_NAME)
    );
};
/**
 * @name getBPId
 * returns Business Partner ID from cookies, a Hybris unique user ID
 */
export const getBPId = (): string | undefined => {
    return Cookies.get(BP_ID_COOKIE_NAME);
};

export const getSessionExpireTime = (): number | null => {
    const sessionExpireTime = parseInt(Cookies.get(HYBRIS_SESSION_EXPIRE_COOKIE_NAME) || '');

    return isNaN(sessionExpireTime) ? null : sessionExpireTime;
};

export const extendSessionExpireTime = (timeDiff: number) => {
    const sessionExpireTime = parseInt(Cookies.get(HYBRIS_SESSION_EXPIRE_COOKIE_NAME) || '');

    if (isNaN(sessionExpireTime)) {
        return;
    }

    Cookies.set(HYBRIS_SESSION_EXPIRE_COOKIE_NAME, (sessionExpireTime + timeDiff).toString(), { path: '/' });
};
